import React, { Fragment, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, Button, Form, Badge, Spinner } from "react-bootstrap";
import {
  FaArrowLeft,
  FaFilePdf,
  FaVideo,
  FaFileAlt,
  FaLink,
  FaUpload,
  FaUsers,
  FaSave,
} from "react-icons/fa";
import api, { getFileUrl } from "../../api";
import PageTitle from "../layouts/PageTitle";
import FilePreview from "../components/Common/FilePreview";
import swal from "sweetalert";
import usePermission from "../../hooks/usePermission";

const VIDEO_EXTS = /\.(mp4|mov|avi|mkv|webm|m4v|wmv|flv)$/i;

const UserGuideAdd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const { hasPermission } = usePermission();
  const canManageGuides =
    hasPermission("create:userguides") || hasPermission("manage:userguides");

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [videoInputMode, setVideoInputMode] = useState("url"); // "url" | "file"
  const [currentFileUrl, setCurrentFileUrl] = useState("");

  const [form, setForm] = useState({
    title: "",
    description: "",
    type: "text", // text | pdf | video
    content: "",
    file: null,
    videoUrl: "",
    category: "general",
    allowedRoles: [], // [] = everyone, otherwise role name strings
  });

  useEffect(() => {
    fetchRoles();
    if (isEdit) {
      fetchGuideDetails();
    }
  }, [id]);

  const fetchRoles = async () => {
    try {
      const res = await api.get("/roles?limit=999");
      setRoles(res.data.data || []);
    } catch (err) {
      console.error("fetchRoles error:", err);
    }
  };

  const fetchGuideDetails = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/user-guides/${id}`);
      if (res.data?.success && res.data?.data) {
        const guide = res.data.data;
        const hasVideoFile = guide.type === "video" && guide.fileUrl;
        setVideoInputMode(hasVideoFile ? "file" : "url");
        setCurrentFileUrl(guide.fileUrl || "");
        setForm({
          title: guide.title || "",
          description: guide.description || "",
          type: guide.type || "text",
          content: guide.content || "",
          file: null,
          videoUrl: guide.videoUrl || "",
          category: guide.category || "general",
          allowedRoles: guide.allowedRoles || [],
        });
      }
    } catch (err) {
      console.error("fetchGuideDetails error:", err);
      swal("Error", err.response?.data?.message || "Failed to load guide", "error");
      navigate("/user-guides");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setForm((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const toggleRole = (roleName) => {
    setForm((prev) => {
      const already = prev.allowedRoles.includes(roleName);
      return {
        ...prev,
        allowedRoles: already
          ? prev.allowedRoles.filter((r) => r !== roleName)
          : [...prev.allowedRoles, roleName],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.title.trim()) {
      swal("Validation Error", "Title is required", "warning");
      return;
    }

    if (form.type === "text" && !form.content.trim()) {
      swal("Validation Error", "Content is required for text guides", "warning");
      return;
    }

    if (form.type === "pdf" && !isEdit && !form.file) {
      swal("Validation Error", "Please select a PDF file to upload", "warning");
      return;
    }

    if (form.type === "video") {
      if (videoInputMode === "url" && !form.videoUrl.trim()) {
        swal("Validation Error", "Please provide a video URL", "warning");
        return;
      }
      if (videoInputMode === "file" && !isEdit && !form.file) {
        swal("Validation Error", "Please select a video file to upload", "warning");
        return;
      }
    }

    const confirmed = await swal({
      title: isEdit ? "Update this guide?" : "Add this guide?",
      icon: "warning",
      buttons: true,
    });
    if (!confirmed) return;

    try {
      setSaving(true);
      const formData = new FormData();
      formData.append("title", form.title);
      formData.append("description", form.description);
      formData.append("type", form.type);
      formData.append("category", form.category);
      formData.append("allowedRoles", JSON.stringify(form.allowedRoles));

      if (form.type === "text") {
        formData.append("content", form.content);
      } else if (form.type === "video") {
        if (videoInputMode === "file" && form.file) {
          formData.append("file", form.file);
        } else {
          formData.append("videoUrl", form.videoUrl);
        }
      } else if (form.type === "pdf" && form.file) {
        formData.append("file", form.file);
      }

      const config = { headers: { "Content-Type": "multipart/form-data" } };
      let res;
      if (isEdit) {
        res = await api.patch(`/user-guides/${id}`, formData, config);
      } else {
        res = await api.post("/user-guides", formData, config);
      }

      if (res.data?.success) {
        swal(
          "Success!",
          `Guide ${isEdit ? "updated" : "added"} successfully!`,
          "success"
        );
        navigate("/user-guides");
      }
    } catch (err) {
      console.error("handleSubmit error:", err);
      swal("Error", err.response?.data?.message || "Failed to save guide", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <PageTitle
        activeMenu={isEdit ? "Edit Guide" : "Add Guide"}
        motherMenu="User Guides"
      />

      <Row>
        <Col lg={12}>
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <Card.Title className="mb-0">
                {isEdit ? "Edit Guide" : "Add New Guide"}
              </Card.Title>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => navigate("/user-guides")}
              >
                <FaArrowLeft className="me-1" /> Back
              </Button>
            </Card.Header>

            <Card.Body className="p-4">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-2 text-muted">Loading guide details...</p>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  {/* Title */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">
                      Title <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      placeholder="e.g. How to Create a Work Order"
                      required
                      className="form-control-lg fs-6"
                    />
                  </Form.Group>

                  {/* Description */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold">Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      placeholder="Brief description of the guide"
                    />
                  </Form.Group>

                  {/* Category & Guide Type */}
                  <Row className="mb-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          Category <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="category"
                          value={form.category}
                          onChange={handleChange}
                          required
                          style={{ height: "42px", borderRadius: "8px" }}
                        >
                          <option value="general">General</option>
                          <option value="onboarding">Onboarding</option>
                          <option value="technical">Technical</option>
                          <option value="admin">Admin</option>
                          <option value="safety">Safety</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          Guide Type <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          name="type"
                          value={form.type}
                          onChange={handleChange}
                          required
                          style={{ height: "42px", borderRadius: "8px" }}
                        >
                          <option value="text">Step-by-Step Text</option>
                          <option value="pdf">PDF Document</option>
                          <option value="video">Video</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Visible To / Audience */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-bold d-flex align-items-center">
                      <FaUsers className="me-2 text-primary" />
                      Visible To
                    </Form.Label>
                    <div className="border rounded-3 p-3 user-guide-media-box">
                      <div className="mb-2">
                        <Form.Check
                          type="checkbox"
                          id="role-everyone"
                          label={<strong>Everyone (all roles)</strong>}
                          checked={form.allowedRoles.length === 0}
                          onChange={() =>
                            setForm((prev) => ({ ...prev, allowedRoles: [] }))
                          }
                        />
                      </div>
                      <hr className="my-2" />
                      <p className="text-muted small mb-2">
                        Or select specific roles — only those roles will see this guide:
                      </p>
                      <Row className="g-2">
                        {roles.map((role) => (
                          <Col xs={6} md={4} lg={3} key={role._id}>
                            <Form.Check
                              type="checkbox"
                              id={`role-${role._id}`}
                              label={role.name}
                              checked={form.allowedRoles.includes(role.name)}
                              onChange={() => toggleRole(role.name)}
                            />
                          </Col>
                        ))}
                      </Row>
                      {form.allowedRoles.length > 0 && (
                        <div className="mt-3 pt-2 border-top">
                          <small className="text-muted me-2">Selected Roles:</small>
                          {form.allowedRoles.map((r) => (
                            <Badge key={r} bg="primary" className="me-1">
                              {r}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Group>

                  {/* Content for TEXT */}
                  {form.type === "text" && (
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        Content <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={12}
                        name="content"
                        value={form.content}
                        onChange={handleChange}
                        placeholder="Write step-by-step instructions here…"
                        required
                        style={{ fontFamily: "inherit" }}
                      />
                    </Form.Group>
                  )}

                  {/* PDF Upload */}
                  {form.type === "pdf" && (
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">
                        Upload PDF <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={handleFileChange}
                        required={!isEdit && !currentFileUrl}
                      />
                      {isEdit && currentFileUrl && !form.file && (
                        <div className="mt-3">
                          <FilePreview file={currentFileUrl} label="Current file" />
                        </div>
                      )}
                    </Form.Group>
                  )}

                  {/* Video Upload or URL */}
                  {form.type === "video" && (
                    <div className="mb-4">
                      <Form.Label className="fw-bold">Video Source</Form.Label>
                      <div className="mb-3 d-flex gap-4">
                        <Form.Check
                          type="radio"
                          id="videoModeUrl"
                          label={
                            <>
                              <FaLink className="me-1" /> External URL
                            </>
                          }
                          checked={videoInputMode === "url"}
                          onChange={() => {
                            setVideoInputMode("url");
                            setForm((p) => ({ ...p, file: null }));
                          }}
                        />
                        <Form.Check
                          type="radio"
                          id="videoModeFile"
                          label={
                            <>
                              <FaUpload className="me-1" /> Upload Video File
                            </>
                          }
                          checked={videoInputMode === "file"}
                          onChange={() => {
                            setVideoInputMode("file");
                            setForm((p) => ({ ...p, videoUrl: "" }));
                          }}
                        />
                      </div>

                      {videoInputMode === "url" && (
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            Video URL <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="url"
                            name="videoUrl"
                            value={form.videoUrl}
                            onChange={handleChange}
                            placeholder="https://youtube.com/watch?v=…"
                            required={videoInputMode === "url"}
                          />
                          <Form.Text className="text-muted">
                            Supported: YouTube, Vimeo, or any direct video URL
                          </Form.Text>
                        </Form.Group>
                      )}

                      {videoInputMode === "file" && (
                        <Form.Group className="mb-3">
                          <Form.Label className="fw-bold">
                            Upload Video <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="file"
                            accept="video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm,.mp4,.mov,.avi,.mkv,.webm,.m4v,.wmv,.flv"
                            onChange={handleFileChange}
                            required={videoInputMode === "file" && !isEdit && !currentFileUrl}
                          />
                          <Form.Text className="text-muted">
                            MP4, MOV, AVI, MKV, WEBM, M4V, WMV, FLV — max 500 MB
                          </Form.Text>
                          {isEdit &&
                            currentFileUrl &&
                            VIDEO_EXTS.test(currentFileUrl) &&
                            !form.file && (
                              <div className="mt-2 text-muted small">
                                <FaVideo className="me-1" /> Current video on file —
                                upload new to replace.
                              </div>
                            )}
                        </Form.Group>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="d-flex justify-content-end gap-2 pt-3 border-top mt-4">
                    <Button
                      variant="secondary"
                      size="lg"
                      className="px-4 rounded-pill"
                      onClick={() => navigate("/user-guides")}
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      className="px-5 rounded-pill shadow"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving…
                        </>
                      ) : (
                        <>
                          <FaSave className="me-2" />
                          {isEdit ? "Update Guide" : "Add Guide"}
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Fragment>
  );
};

export default UserGuideAdd;

